import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/Layout';
// Eager load PricingPage
import { PricingPage } from '@/pages/pricing';
import { ProtectedRoute } from '@/components/protected-route';
import { ErrorBoundary } from '@/components/error-boundary';
import { SecurityProvider } from '@/components/security-provider';
import { ConversionProvider } from '@/contexts/ConversionContext';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from 'sonner';

// Lazily-loaded Page Components
const HomePage = React.lazy(() => import('@/pages/home').then(m => ({ default: m.HomePage })));
const UploadPage = React.lazy(() => import('@/pages/upload').then(m => ({ default: m.UploadPage })));
const DashboardPage = React.lazy(() => import('@/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const LoginPage = React.lazy(() => import('@/pages/login').then(m => ({ default: m.LoginPage })));
// PricingPage is eager loaded to prevent issues with Stripe redirect back navigation
const TemplatesPage = React.lazy(() => import('@/pages/templates').then(m => ({ default: m.TemplatesPage })));
const ConverterPage = React.lazy(() => import('@/pages/converter').then(m => ({ default: m.ConverterPage })));
const SettingsPage = React.lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })));
const ContactPage = React.lazy(() => import('@/pages/contact').then(m => ({ default: m.ContactPage })));
const TutorialPage = React.lazy(() => import('@/pages/tutorial').then(m => ({ default: m.TutorialPage })));
const TermsPage = React.lazy(() => import('@/pages/terms').then(m => ({ default: m.TermsPage })));
const PrivacyPage = React.lazy(() => import('@/pages/privacy').then(m => ({ default: m.PrivacyPage })));
const FAQPage = React.lazy(() => import('@/pages/faq').then(m => ({ default: m.FAQPage })));
const DocsPage = React.lazy(() => import('@/pages/docs').then(m => ({ default: m.DocsPage })));
const SuccessPage = React.lazy(() => import('@/pages/success').then(m => ({ default: m.SuccessPage })));
const BlogPage = React.lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogArticlePage = React.lazy(() => import('@/pages/BlogArticlePage').then(m => ({ default: m.BlogArticlePage })));
const NotFoundPage = React.lazy(() => import('@/pages/not-found').then(m => ({ default: m.NotFoundPage })));
const ServerErrorPage = React.lazy(() => import('@/pages/500').then(m => ({ default: m.ServerErrorPage })));
const HubPage = React.lazy(() => import('@/pages/HubPage').then(m => ({ default: m.HubPage })));
const TemplateDetailsPage = React.lazy(() => import('@/pages/TemplateDetailsPage').then(m => ({ default: m.TemplateDetailsPage })));
const SupportedNodesPage = React.lazy(() => import('@/pages/supported-nodes').then(m => ({ default: m.SupportedNodesPage })));
const DeploymentGuidePage = React.lazy(() => import('@/pages/deployment-guide').then(m => ({ default: m.DeploymentGuidePage })));
const A11yAuditPage = React.lazy(() => import('@/pages/a11y-audit').then(m => ({ default: m.A11yAuditPage })));

// Simple fallback
const Fallback = () => (
  <div className="w-full py-10 text-center text-sm text-muted-foreground">Loading…</div>
);

function App() {
  const navigate = useNavigate();

  const handleRedirectToLogin = () => {
    navigate('/login');
  };

  // Idle prefetch of common routes
  useEffect(() => {
    const idle = (cb: () => void) =>
      ('requestIdleCallback' in window ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 200));
    idle(() => {
      // Trigger webpack/vite to prefetch chunks
      import('@/pages/upload');
      // import('@/pages/pricing'); // Eager loaded
      import('@/pages/tutorial');
      import('@/pages/docs');
      import('@/pages/login');
      import('@/pages/supported-nodes');
      import('@/pages/templates');
    });
  }, []);

  // Scroll to top on route change
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <Toaster position="top-center" richColors />
        <SecurityProvider>
          <ConversionProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout><Suspense fallback={<Fallback />}><HomePage /></Suspense></Layout>} />
              <Route path="/login" element={<Suspense fallback={<Fallback />}><LoginPage /></Suspense>} />
              <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
              <Route path="/contact" element={<Layout><Suspense fallback={<Fallback />}><ContactPage /></Suspense></Layout>} />
              <Route path="/converter" element={
                <ProtectedRoute onRedirectToLogin={handleRedirectToLogin}>
                  <Layout><Suspense fallback={<Fallback />}><ConverterPage /></Suspense></Layout>
                </ProtectedRoute>
              } />
              <Route path="/tutorial" element={<Layout><Suspense fallback={<Fallback />}><TutorialPage /></Suspense></Layout>} />
              <Route path="/terms" element={<Layout><Suspense fallback={<Fallback />}><TermsPage /></Suspense></Layout>} />
              <Route path="/privacy" element={<Layout><Suspense fallback={<Fallback />}><PrivacyPage /></Suspense></Layout>} />
              <Route path="/faq" element={<Layout><Suspense fallback={<Fallback />}><FAQPage /></Suspense></Layout>} />
              <Route path="/docs" element={<Layout><Suspense fallback={<Fallback />}><DocsPage /></Suspense></Layout>} />
              <Route path="/blog" element={<Layout><Suspense fallback={<Fallback />}><BlogPage /></Suspense></Layout>} />
              <Route path="/blog/:slug" element={<Layout><Suspense fallback={<Fallback />}><BlogArticlePage /></Suspense></Layout>} />
              <Route path="/success" element={<Layout><Suspense fallback={<Fallback />}><SuccessPage /></Suspense></Layout>} />
              <Route path="/supported-nodes" element={<Layout><Suspense fallback={<Fallback />}><SupportedNodesPage /></Suspense></Layout>} />
              <Route path="/deployment-guide" element={<Layout><Suspense fallback={<Fallback />}><DeploymentGuidePage /></Suspense></Layout>} />
              <Route path="/a11y" element={<Layout><Suspense fallback={<Fallback />}><A11yAuditPage /></Suspense></Layout>} />
              <Route path="/templates" element={<Layout><Suspense fallback={<Fallback />}><TemplatesPage /></Suspense></Layout>} />
              <Route path="/template/:id" element={<Layout><Suspense fallback={<Fallback />}><TemplateDetailsPage /></Suspense></Layout>} />

              {/* Protected Routes */}
              <Route path="/upload" element={
                <ProtectedRoute onRedirectToLogin={handleRedirectToLogin}>
                  <Layout><Suspense fallback={<Fallback />}><UploadPage /></Suspense></Layout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute onRedirectToLogin={handleRedirectToLogin}>
                  <Layout><Suspense fallback={<Fallback />}><DashboardPage /></Suspense></Layout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute onRedirectToLogin={handleRedirectToLogin}>
                  <Layout><Suspense fallback={<Fallback />}><SettingsPage /></Suspense></Layout>
                </ProtectedRoute>
              } />
              <Route path="/hub" element={
                <ProtectedRoute onRedirectToLogin={handleRedirectToLogin}>
                  <Layout><Suspense fallback={<Fallback />}><HubPage /></Suspense></Layout>
                </ProtectedRoute>
              } />

              {/* Error Pages */}
              <Route path="/500" element={<Suspense fallback={<Fallback />}><ServerErrorPage /></Suspense>} />
              <Route path="/404" element={<Suspense fallback={<Fallback />}><NotFoundPage /></Suspense>} />
              <Route path="*" element={<Suspense fallback={<Fallback />}><NotFoundPage /></Suspense>} />
            </Routes>
          </ConversionProvider>
        </SecurityProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
